<template>
  <div>
    <div class="container-fluid first-view" v-if="step < 6">
      <div class="background-overlay"/>
      <action-alert
        :type="alert.type"
        :icon="alert.icon"
        :is-visible="alert.isVisible"
      >
        {{ alert.text }}
      </action-alert>
      <img class="logo" :src="logo" />
      <RDFFirstForm
        :form-data="formData"
        @update:formData="formData = $event"
        :fetching-data="fetchingData"
        :study-id="studyId"
        :start-from-beginning="startFromBeginning"
        :rdf-data="rdfData"
        :loading-data="loadingData"
        :step="step"
        :metrics-data="metricsData"
        :product-codes-data="productCodesData"
        :product-codes-columns-data="productCodesColumnsData"
        :binary-question-data="binaryQuestionData"
        :handle-change="handleChange"
        :check-required-data="checkRequiredData"
        :download-file="downloadFile"
        @next-page="nextPage"
        @prev-page="prevPage"
      />
    </div>
    <div v-if="step === 6">
      <RDFResults
        :loading-data="loadingData"
        :rdf-data="rdfData"
        :show-charts="formData.charts"
        :show-tabs="true"
        :download-file="downloadFile"
      />
    </div>
  </div>
</template>

<script>
import logo from '@/assets/images/logos/Iris-Grey.svg'
import { mapActions, mapGetters } from 'vuex'
import generalGetters from '../store/modules/general/getterTypes'
import ActionAlert from '../components/shared/ActionAlert'
import studiesActions from '@/store/modules/studies/actionTypes'
import RDFFirstForm from '../components/RDFForms/RDFFirstForm'
import RDFResults from '@/components/RDFForms/RDFResults'
import globalAlertMixin from '@/mixins/globalAlert'

export default {
  name: 'RDFUpload',
  mixins: [globalAlertMixin],
  components: {
    RDFFirstForm,
    RDFResults,
    ActionAlert
  },
  data () {
    return {
      formData: {
        study_name: '',
        data_input: '',
        likert_scale: '',
        folder_name: '',
        product_code: '',
        paired: true,
        parametric: false,
        product_order: '',
        main_question_column: '',
        strength_questions: '',
        test_types: ['two_99', 'two_95', 'two_90'],
        cpv_question: '',
        product_code_order: [],
        benchmark_product_code: [],
        benchmark_product_code_order: [],
        candidate_product_code_order: [],
        regression: false,
        regression_input: '',
        replace_product_codes: false,
        replace_product_input: '',
        display_correlation: false,
        charts: false,
        metricType: '',
        metric: '',
        aggregation: '',
        binary_option: 'No binary questions'
      },
      alert: {
        type: '',
        icon: '',
        text: '',
        isVisible: false
      },
      logo,
      studyId: this.$route.params.id || '',
      metricsData: [],
      productCodesData: [],
      productCodesColumnsData: [],
      binaryQuestionData: [
        { label: 'No binary questions', value: 'No binary questions' },
        { label: '0 and 1', value: '0 and 1' },
        { label: '1 and 2', value: '1 and 2' }
      ],
      startFromBeginning: true,
      fetchingData: false,
      loadingData: false,
      rdfData: null,
      step: this.$route.params.id ? 2 : 0
    }
  },
  computed: {
    ...mapGetters({
      alertMessage: generalGetters.GET_ALERT_MESSAGE,
      alertType: generalGetters.GET_ALERT_TYPE
    })
  },
  methods: {
    ...mapActions({
      sendRdfData: studiesActions.SEND_RDF_DATA,
      getStudyMetrics: studiesActions.GET_STUDY_METRICS,
      uploadRdfData: studiesActions.UPLOAD_RDF_DATA,
      checkAllCloseEndedQuestions: studiesActions.CHECK_ALL_CLOSE_ENDED_QUESTIONS
    }),
    handleChange (file, field) {
      if (file.name.endsWith('.csv')) {
        this.formData[field] = file.raw
      } else {
        this.showAlert('Invalid file extension')
      }
    },
    checkRequiredData (requiredFields = [], checkFieldBeforeValid = '', fieldToCheck = '', fieldValue = '') {
      let errors = []
      let e = []
      if (!requiredFields.length) return

      requiredFields.forEach(field => {
        const selectedData = this.formData[field['key']]
        const isFieldAnArray = Array.isArray(selectedData)
        if (fieldToCheck && fieldValue) {
          if (this.formData[fieldToCheck] === fieldValue) {
            if (isFieldAnArray && !selectedData.length) errors.push(`The array ${field['name']} is empty!`)
            else if (!isFieldAnArray && !selectedData) e.push(`Missing value for ${field['name']}`)
          }
        } else if (checkFieldBeforeValid) {
          if (((isFieldAnArray && !selectedData.length) || (!isFieldAnArray && !selectedData)) && this.formData[checkFieldBeforeValid]) {
            isFieldAnArray ? errors.push(`The array ${field['name']} is empty!`) : e.push(`Missing value for ${field['name']}`)
          }
        } else {
          if (isFieldAnArray && !selectedData.length) errors.push(`The array ${field['name']} is empty!`)
          else if (!isFieldAnArray && !selectedData) e.push(`Missing value for ${field['name']}`)
        }
      })
      return errors
    },
    downloadFile (fileName = null) {
      if (!fileName) {
        this.showAlert(`Not ready yet! ${fileName}`)
        return
      }

      this.axios({
        url: `rdf/download_templates/${this.formData.folder_name}/${fileName}`,
        method: 'GET',
        responseType: 'blob'
      }).then(res => {
        const url = window.URL.createObjectURL(new Blob([res.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `${fileName}`)
        document.body.appendChild(link)
        link.click()
      })
    },
    showAlert (title, type = 'error', duration = 5000) {
      const options = {
        duration: duration,
        title: title,
        type: type
      }
      this.launchAlert(options)
    },
    prevPage () {
      this.step -= 1
    },
    async nextPage () {
      if ([5, 0, 1, 3].includes(this.step)) {
        this.loadingData = true
        this.step += 1
        if (this.step === 6) {
          await this.sendFormData()
        } else if (!this.studyId && this.step === 1) {
          await this.uploadRdfDataFile()
        } else if (!this.studyId && this.step === 2) {
          await this.uploadRdfScaleFile()
        } else if (this.step === 4) {
          await this.checkSelectedProductCodeColumn()
        }
        this.loadingData = false
      } else this.step += 1
    },
    async getMetrics () {
      if (this.studyId) {
        try {
          const data = await this.getStudyMetrics({
            studyId: this.studyId
          })
          this.metricsData = Array.isArray(data?.metrics) ? data.metrics : []
          this.productCodesData = Array.isArray(data?.product_codes) ? data.product_codes : []
          this.productCodesColumnsData = Array.isArray(data?.product_code_columns) ? data.product_code_columns : []
        } catch {
          this.metricsData = []
          this.productCodesData = []
          this.productCodesColumnsData = []
          this.showAlert('Unable to load study metrics')
        }
      }
    },
    async uploadRdfDataFile () {
      const dataKeys = ['study_name', 'data_input']
      const formData = new FormData()
      dataKeys.forEach(key => formData.append(key, this.formData[key]))
      const data = await this.uploadRdfData({
        formData: formData,
        method: 'POST'
      })
      this.formData.folder_name = data.folder_name
      this.productCodesData = data.product_codes
      this.productCodesColumnsData = data.product_code_columns
    },
    async uploadRdfScaleFile () {
      const dataKeys = ['folder_name', 'likert_scale']
      const formData = new FormData()
      dataKeys.forEach(key => formData.append(key, this.formData[key]))
      const data = await this.uploadRdfData({
        formData: formData,
        method: 'PUT'
      })
      if (data.error) {
        this.showAlert(data.error)
        this.step -= 1
      } else {
        this.metricsData = data.metrics
      }
    },
    async checkSelectedProductCodeColumn () {
      const formData = new FormData()
      if (this.studyId) {
        formData.append('study_id', this.studyId)
        formData.append('product_code', this.formData.product_code)
      } else {
        formData.append('folder_name', this.formData.folder_name)
        formData.append('product_code', this.formData.product_code)
      }
      const data = await this.checkAllCloseEndedQuestions({
        formData: formData
      })
      if (data.error) {
        this.showAlert(data.error)
        this.step -= 1
      }
    },
    async sendFormData () {
      this.rdfData = null
      const data = await this.sendRdfData({
        dataForm: this.formData,
        studyId: this.studyId
      })
      if (data.error) {
        this.showAlert(data.error)
        this.step -= 1
      } else {
        this.rdfData = data
        this.formData.folder_name = this.rdfData['study_folder_name']
      }
    }
  },
  async mounted () {
    if (this.studyId) {
      this.fetchingData = true
      this.startFromBeginning = false
      await this.getMetrics()
      this.fetchingData = false
    }
  }
}
</script>

<style lang="scss" scoped>
.container-fluid.first-view {
  padding: 99px 87px !important;
  position: absolute;
  background-image: url("../assets/images/backgrounds/IRIS-background-720p.jpg");
  background-position: center;
  background-size: cover;
  display: flex;
  justify-content: center;
  align-items: center;
  .background-overlay {
    position: absolute;
    width: 100%;
    height: 100%;
    background-color: #2a274b;
    opacity: 0.4;
  }
}

.logo {
  position: fixed;
  bottom: 60px;
  right: 25px;
  max-width: 300px;
  min-width: 120px;
  max-height: 300px;
  min-height: 120px;
  width: 10%;
  height: auto;
}
@media (max-height: 980px) {
  .logo {
    bottom: 40px;
    right: 15px;
    width: 6%;
  }
}

.container-fluid {
  height: 100%;
}
</style>
